import * as React from "react"
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  cn,
} from "@nexus/ui"
import { AI_EDIT_INSTRUCTION_MAX_LENGTH } from "@nexus/contracts"
import { useTranslation } from "react-i18next"
import { getScriptDisplayTitle, isUsableScriptText } from "@/features/opportunities/lib/script-display-title"

/** Show the counter once the user has typed this fraction of the max length. */
const COUNTER_VISIBLE_THRESHOLD_RATIO = 0.9
const COUNTER_VISIBLE_FROM = AI_EDIT_INSTRUCTION_MAX_LENGTH * COUNTER_VISIBLE_THRESHOLD_RATIO

// Hardcoded English — these are LLM prompt templates, not UI strings
const PROMPT_TEMPLATES = [
  "Make the hook more attention-grabbing",
  "Shorten to 30 seconds",
  "Make the tone more casual",
  "Strengthen the CTA",
  "Add more urgency to the closing scene",
  "Rewrite for a younger audience",
  "Make the product demo clearer",
  "Cut the script to the essential scenes only",
]

/** Minimal shape the modal needs to render previews — keeps it decoupled from ScriptDto. */
export interface ScriptPreview {
  id: string
  hook: string | null
  concept?: string | null
  opportunityTitle: string | null
}

export interface AiScriptEditorModalProps {
  open: boolean
  onClose: () => void
  /** One script for single edit, multiple for bulk. The modal renders a preview/list of these. */
  scripts: ScriptPreview[]
  onSubmit: (instruction: string) => void
  isSubmitting?: boolean
  error?: string | null
}

export function AiScriptEditorModal({
  open,
  onClose,
  scripts,
  onSubmit,
  isSubmitting = false,
  error,
}: AiScriptEditorModalProps) {
  const { t } = useTranslation()
  const [instruction, setInstruction] = React.useState("")
  const [listOpen, setListOpen] = React.useState(false)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const isBulk = scripts.length > 1
  const count = scripts.length

  React.useEffect(() => {
    if (open) {
      setInstruction("")
      setListOpen(false)
      requestAnimationFrame(() => textareaRef.current?.focus())
    }
  }, [open])

  function handleSubmit() {
    const trimmed = instruction.trim()
    if (!trimmed || isSubmitting) return
    onSubmit(trimmed)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  function applyTemplate(text: string) {
    setInstruction(text)
    textareaRef.current?.focus()
  }

  const canSubmit = instruction.trim().length > 0 && !isSubmitting

  // Closing must always work: the edit runs in the background (toast-tracked)
  // independently of this modal, so dismissing never loses work. Gating close on
  // `isSubmitting` trapped the modal when it was reopened while a prior edit for a
  // different script was still in flight (shared mutation pending state). See AD-1648.
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-2xl gap-0 p-0">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--ai-fill-chip)]">
            <Icon name="sparkles" size="sm" className="text-[var(--ai-text)]" />
          </div>
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-base font-semibold leading-snug">
              {isBulk
                ? t("opportunities.scripts.aiEditor.bulkTitle")
                : t("opportunities.scripts.aiEditor.title")}
            </DialogTitle>
            {isBulk && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t("opportunities.scripts.aiEditor.bulkSubtitle", { count })}
              </p>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-6 py-5">
          <p className="text-sm text-muted-foreground">
            {isBulk
              ? t("opportunities.scripts.aiEditor.bulkDescription", { count })
              : t("opportunities.scripts.aiEditor.description")}
          </p>

          {/* Single-mode: preview the one script so the user sees what they're editing. */}
          {!isBulk && scripts[0] && (
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
              <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground">
                {getScriptDisplayTitle(
                  { hook: scripts[0].hook, concept: scripts[0].concept ?? null },
                  t("opportunities.scripts.aiEditor.untitled"),
                )}
              </p>
              {scripts[0].opportunityTitle && (
                <p className="mt-1 truncate text-[11px] text-muted-foreground">
                  {scripts[0].opportunityTitle}
                </p>
              )}
            </div>
          )}

          {/* Bulk-mode: collapsible list of every script in the batch so users don't
              fly blind into an irreversible operation. */}
          {isBulk && scripts.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
              <button
                type="button"
                onClick={() => setListOpen((v) => !v)}
                aria-expanded={listOpen}
                className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <Icon name="rows-3" size="xs" className="text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground">
                    {t("opportunities.scripts.aiEditor.scriptListToggle", { count })}
                  </span>
                </div>
                <Icon
                  name="chevron-right"
                  size="xs"
                  className={cn(
                    "shrink-0 text-muted-foreground transition-transform duration-200",
                    listOpen && "rotate-90",
                  )}
                />
              </button>
              <div
                className={cn(
                  "grid transition-all duration-200 ease-in-out",
                  listOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
              >
                <div className="overflow-hidden">
                  <ul className="max-h-48 divide-y divide-border/40 overflow-y-auto border-t border-border/40">
                    {scripts.map((s, i) => (
                      <li key={s.id} className="flex items-start gap-3 px-3 py-2">
                        <span className="mt-0.5 w-6 shrink-0 text-[10px] font-semibold tabular-nums text-muted-foreground/60">
                          {i + 1}.
                        </span>
                        <span
                          className={cn(
                            "line-clamp-2 min-w-0 flex-1 text-xs leading-snug",
                            isUsableScriptText(s.hook) || isUsableScriptText(s.concept)
                              ? "text-foreground"
                              : "italic text-muted-foreground",
                          )}
                        >
                          {getScriptDisplayTitle(
                            { hook: s.hook, concept: s.concept ?? null },
                            t("opportunities.scripts.aiEditor.untitled"),
                          )}
                        </span>
                        {s.opportunityTitle && (
                          <span className="ml-2 max-w-[40%] shrink-0 truncate text-[10px] text-muted-foreground/70">
                            {s.opportunityTitle}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Template chips */}
          <div className="flex flex-wrap gap-2">
            {PROMPT_TEMPLATES.map((tpl) => (
              <button
                key={tpl}
                type="button"
                onClick={() => applyTemplate(tpl)}
                disabled={isSubmitting}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors disabled:pointer-events-none disabled:opacity-50",
                  instruction === tpl
                    ? "border-primary/40 bg-primary/5 text-foreground"
                    : "border-border text-muted-foreground hover:border-border/80 hover:bg-muted hover:text-foreground",
                )}
              >
                {tpl}
              </button>
            ))}
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("opportunities.scripts.aiEditor.placeholder")}
            rows={3}
            maxLength={AI_EDIT_INSTRUCTION_MAX_LENGTH}
            disabled={isSubmitting}
            className={cn(
              "w-full resize-none rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none transition-[color,box-shadow]",
              "placeholder:text-muted-foreground/50",
              "focus:border-[var(--ai)] focus:ring-[3px] focus:ring-[var(--ai-fill-chip)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error ? "border-destructive" : "border-input",
            )}
          />

          {instruction.length >= COUNTER_VISIBLE_FROM && (
            <p
              role="status"
              aria-live="polite"
              className="-mt-2 self-end text-[11px] tabular-nums text-muted-foreground"
            >
              {t("opportunities.scripts.aiEditor.characterCount", {
                current: instruction.length,
                max: AI_EDIT_INSTRUCTION_MAX_LENGTH,
                defaultValue: "{{current}} / {{max}}",
              })}
            </p>
          )}

          {/* Error */}
          {error && (
            <p className="flex items-center gap-1.5 text-xs text-destructive">
              <Icon name="alert-circle" size="xs" />
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border/60 px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {t("opportunities.scripts.aiEditor.pressEnterHint")}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              iconStart={isSubmitting ? "loader" : "sparkles"}
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={isSubmitting ? "[&_svg]:animate-spin" : ""}
            >
              {isBulk
                ? t("opportunities.scripts.aiEditor.submitBulk", { count })
                : t("opportunities.scripts.aiEditor.submitSingle")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
